import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/Button';
import { WELCOME_DISCLAIMER, WELCOME_ACK_STORAGE_KEY, LEGACY_WELCOME_ACK_KEYS } from '@/lib/disclaimers';

/**
 * v129 — First-time / version-bump welcome modal.
 * v130.1 — Sticky-footer + visual-hierarchy patch (CTA was below the fold).
 * v130.2 — Required-acceptance pass. The modal is now a blocking
 *          acknowledgment: there is no X, no backdrop-tap dismiss, no
 *          Escape-to-dismiss. The ONLY path forward is the primary CTA,
 *          which writes the localStorage ack. Aligns the UX with the
 *          legal protection language — coverage only attaches when the
 *          user has explicitly accepted.
 *
 * Gate: localStorage key `sparrowForgeWelcomeAck.v129` (defined in disclaimers.ts).
 * - Not set → modal shows on mount.
 * - Set to "1" → modal stays hidden.
 * - Bumping the suffix in disclaimers.ts (e.g. .v130) re-prompts every user when
 *   the master language materially changes — that's the contract.
 *
 * Blocking behavior (v130.2):
 *   - No X close button.
 *   - Backdrop tap does NOTHING except trigger a brief "shake" animation on
 *     the modal panel, signaling "please use the accept button below."
 *   - Escape key does nothing.
 *   - Page underneath is fully blocked (body scroll lock + focus trap).
 *   - Primary CTA "I understand — let me start building" is the only path
 *     forward. It writes the ack and closes.
 *   - Secondary link "Read full Safety & Legal Notes" opens /pages/safety in
 *     a new tab and does NOT dismiss the modal. User can read the full
 *     disclaimer then come back and accept.
 *
 * A11y:
 *   - role="alertdialog" + aria-modal="true" — communicates to screen
 *     readers that this is a blocking acknowledgment, not a regular dialog.
 *   - aria-labelledby on the heading, aria-describedby on the disclaimer body.
 *   - Focus is trapped inside the modal (Tab/Shift+Tab cycle within it).
 *   - Initial focus lands on the primary CTA so a returning user can tap
 *     accept immediately on re-render.
 *
 * Memory: modal portal required — see [[feedback_modal_portal_required]].
 */
export function WelcomeModal() {
  const [open, setOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const acceptBtnRef = useRef<HTMLButtonElement | null>(null);
  const safetyLinkRef = useRef<HTMLAnchorElement | null>(null);

  /**
   * v132.1 — Ack lookup is multi-step because the Forge runs as an iframe whose
   * origin (cdn.shopify.com) is third-party to the storefront. Safari ITP can
   * blackhole the iframe's own localStorage so the ack never persists across
   * visits. We layer three checks so the ack survives any one of them failing:
   *
   *   1. Direct localStorage at WELCOME_ACK_STORAGE_KEY (new) or any legacy key
   *   2. postMessage handshake to the parent storefront, which reads + replies
   *      with a value from the *parent* localStorage (same first-party origin
   *      as sparrowexpeditions.com — survives ITP).
   *   3. Fall back to "show the modal" only if both above fail and we have no
   *      record of a prior ack.
   *
   * The handshake uses a 500 ms timeout so the modal doesn't flash visible
   * while we wait — we render hidden until either the parent responds or the
   * timeout elapses.
   */
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    const REQ_ID = `req_${Math.random().toString(36).slice(2, 10)}`;

    // 1. Try the iframe's own localStorage first (covers same-origin case
    //    + non-ITP browsers + users who acked via a legacy key).
    const localAck = readLocalAck();
    if (localAck) {
      // Already acked — no need to ask the parent. Stay closed.
      return;
    }

    // 2. Ask the parent storefront to check its first-party localStorage.
    //    If the bundle isn't in an iframe (window.parent === window), skip.
    const hasParent = typeof window !== 'undefined' && window.parent && window.parent !== window;

    const onParentMessage = (e: MessageEvent) => {
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'sparrowForge.ackResult' || data.reqId !== REQ_ID) return;
      if (cancelled) return;
      cancelled = true;
      window.removeEventListener('message', onParentMessage);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      if (data.acked) {
        // Parent says the user already acknowledged — write to our local
        // store too so subsequent boots don't have to round-trip.
        writeLocalAck();
        // setOpen stays false → modal never shows.
      } else {
        setOpen(true);
      }
    };

    if (hasParent) {
      window.addEventListener('message', onParentMessage);
      try {
        // Targeting '*' is intentional: the parent origin varies by storefront
        // (sparrowexpeditions.com, partner shops, dev shops). The host bridge
        // only ever READS a fixed local key and REPLIES the same payload — no
        // sensitive data is exchanged either way.
        window.parent.postMessage({
          type: 'sparrowForge.readAck',
          reqId: REQ_ID,
          key: WELCOME_ACK_STORAGE_KEY,
          legacyKeys: LEGACY_WELCOME_ACK_KEYS,
        }, '*');
      } catch {
        // postMessage to the parent failed (sandboxed iframe). Fall through.
      }
      // 3. Timeout — if no parent reply in 500 ms, show the modal.
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        cancelled = true;
        window.removeEventListener('message', onParentMessage);
        setOpen(true);
      }, 500);
    } else {
      // Not in an iframe — no parent to ask. localStorage didn't have it.
      setOpen(true);
    }

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      window.removeEventListener('message', onParentMessage);
    };
  }, []);

  // Body scroll lock + focus trap. NO Escape-to-dismiss in v130.2 —
  // acceptance is the only path forward. Tab/Shift+Tab cycles within the
  // modal between the safety link and the primary CTA.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Initial focus → the primary CTA (so a returning user can hit Enter).
    // requestAnimationFrame to wait for the portal to mount.
    const f = requestAnimationFrame(() => acceptBtnRef.current?.focus());

    const onKey = (e: KeyboardEvent) => {
      // Block Escape from closing — required acceptance.
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        triggerShake();
        return;
      }
      // Focus trap: cycle between safety link and accept button.
      if (e.key === 'Tab') {
        const a = safetyLinkRef.current;
        const b = acceptBtnRef.current;
        if (!a || !b) return;
        const focusables: HTMLElement[] = [a, b];
        const active = document.activeElement as HTMLElement | null;
        const idx = focusables.indexOf(active ?? focusables[0]);
        e.preventDefault();
        if (e.shiftKey) {
          const next = focusables[(idx <= 0 ? focusables.length : idx) - 1];
          next.focus();
        } else {
          const next = focusables[(idx + 1) % focusables.length];
          next.focus();
        }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey, true);
      cancelAnimationFrame(f);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const triggerShake = () => {
    setShake(true);
    window.setTimeout(() => setShake(false), 380);
  };

  if (!open) return null;

  const handleAcknowledge = () => {
    // v132.1 — belt + suspenders: write locally AND ask the parent storefront
    // to persist the ack on the first-party origin so Safari ITP can't
    // blackhole the iframe's localStorage.
    writeLocalAck();
    try {
      if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'sparrowForge.writeAck',
          key: WELCOME_ACK_STORAGE_KEY,
          value: '1',
        }, '*');
      }
    } catch { /* sandboxed iframe — local-only persistence will have to do */ }
    setOpen(false);
  };

  // Backdrop click handler — does NOT dismiss in v130.2. Triggers a brief
  // shake on the modal so the user understands the action button is required.
  const handleBackdropClick = () => {
    triggerShake();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9000] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-0 sm:p-4"
      style={{ height: '100dvh' }}
      onClick={handleBackdropClick}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="welcome-modal-title"
      aria-describedby="welcome-modal-body"
    >
      <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full sm:max-w-md bg-zinc-950 border-t sm:border border-zinc-800 rounded-t-2xl sm:rounded-xl shadow-2xl flex flex-col transition-transform ${shake ? 'sx-welcome-shake' : ''}`}
        style={{ maxHeight: '100dvh' }}
      >
        {/* Inline shake keyframes — kept here so the modal stays a single
            self-contained file. Lasts 380ms. */}
        <style>{`
          @keyframes sxWelcomeShake {
            0%,100% { transform: translateX(0); }
            15% { transform: translateX(-6px); }
            30% { transform: translateX(5px); }
            45% { transform: translateX(-4px); }
            60% { transform: translateX(3px); }
            75% { transform: translateX(-2px); }
          }
          .sx-welcome-shake { animation: sxWelcomeShake 380ms cubic-bezier(.36,.07,.19,.97); }
        `}</style>

        {/* ───── HEADER (centered title, ember accent rule, NO X) ───── */}
        <div className="flex-shrink-0">
          <div className="h-1 bg-gradient-to-r from-ember-500 via-ember-400 to-ember-600" />
          <div className="px-5 pt-5 pb-2 text-center">
            <h2
              id="welcome-modal-title"
              className="text-lg sm:text-xl font-bold text-zinc-50 tracking-tight"
            >
              {WELCOME_DISCLAIMER.header}
            </h2>
            <div className="mx-auto mt-2 h-px w-12 bg-ember-500/60" aria-hidden="true" />
          </div>
        </div>

        {/* ───── BODY (scrolls internally) ───── */}
        <div
          id="welcome-modal-body"
          className="flex-1 min-h-0 overflow-y-auto px-5 pb-3 pt-3"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="space-y-3 text-sm leading-relaxed text-zinc-300">
            {WELCOME_DISCLAIMER.body.map((para, i) => {
              if (i === 1 && para.startsWith('Use The Forge with hands-on judgment.')) {
                return (
                  <p key={i}>
                    <strong className="text-zinc-100">Use The Forge with hands-on judgment.</strong>{' '}
                    {para.replace('Use The Forge with hands-on judgment. ', '')}
                  </p>
                );
              }
              return <p key={i}>{para}</p>;
            })}
          </div>

          {/* Secondary link — reading the full safety notes is the off-ramp
              instead of dismissing. Opens in new tab; modal continues to
              block the page underneath. */}
          <div className="mt-4 mb-1 text-center">
            <a
              ref={safetyLinkRef}
              href={WELCOME_DISCLAIMER.secondaryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center min-h-[44px] px-3 text-xs text-cyan-400 hover:text-cyan-300 focus:text-cyan-300 underline decoration-dotted focus:outline-none focus:ring-2 focus:ring-cyan-500/40 rounded touch-manipulation"
            >
              {WELCOME_DISCLAIMER.secondaryLinkLabel}
            </a>
          </div>
        </div>

        {/* ───── STICKY FOOTER (the ONE path forward) ───── */}
        <div className="flex-shrink-0 border-t border-zinc-800 bg-zinc-950 px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            ref={acceptBtnRef}
            variant="primary"
            size="lg"
            onClick={handleAcknowledge}
            className="w-full justify-center min-h-[48px] text-sm sm:text-base font-semibold"
          >
            {WELCOME_DISCLAIMER.primaryCta}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────
// v132.1 — local ack storage helpers (exported for the test suite)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Read an ack from the iframe's OWN localStorage. Returns true if the user
 * has acknowledged under the current key OR any legacy key (so users who
 * accepted the v129 onboarding aren't re-prompted under the v132.1 rename).
 *
 * Wrapped in try/catch because Safari ITP throws SecurityError on bare
 * localStorage access from cross-origin iframes.
 */
export function readLocalAck(
  key: string = WELCOME_ACK_STORAGE_KEY,
  legacyKeys: readonly string[] = LEGACY_WELCOME_ACK_KEYS,
): boolean {
  try {
    if (typeof window === 'undefined') return false;
    if (window.localStorage.getItem(key)) return true;
    for (const legacy of legacyKeys) {
      if (window.localStorage.getItem(legacy)) return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Write the ack to the iframe's OWN localStorage. Silent on failure. */
export function writeLocalAck(key: string = WELCOME_ACK_STORAGE_KEY): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(key, '1');
  } catch { /* sandbox / ITP / quota — silently ignore */ }
}
